import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Braces,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cloud,
  Database,
  FileSpreadsheet,
  Folder,
  Globe,
  Hash,
  KeyRound,
  List,
  MoreHorizontal,
  Plug,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Sigma,
  Table2,
  ToggleLeft,
  Trash2,
  Type as TypeIcon,
  UserPlus,
  Users,
  Wand2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { adminApi } from "../../api/admin";
import {
  dataHubApi,
  type AccessGrant,
  type Catalog,
  type CatalogGroup,
  type CatalogSource,
  type Candidate,
  type CandidateRole,
  type CatalogTable,
  type CleanStep,
  type ConnectResult,
  type ConnectTable,
  type DetectedColumn,
  type SourceSpec,
  type TablePreview,
} from "../../api/dataHub";
import { dataSourceApi, datasetApi, sharePointApi, type SharePointDrive, type SharePointDriveItem, type SharePointList, type SharePointSite } from "../../api/dataSources";
import { useAuth } from "../../auth/AuthContext";
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Menu,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  SkeletonRows,
  Stat,
  Switch,
  Tabs,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";
import type { Dataset } from "../../types";
import { FieldsTab, MeasuresTab, PreviewTab } from "./DatasetsV2Page";
import { MyFilesPanel, useOneDriveStatus } from "./MyFilesPanel";

/**
 * v2.2 "Data": one place for what used to be "Data sources" + "Datasets".
 * People see *tables* grouped by the SharePoint site they live in; the
 * file/list a table comes from is one level in between. "Add data" goes from
 * site → file → sheets in one flow and the columns are detected on the
 * server, so a new table is usable in the designer straight away.
 */

const SOURCE_ICON: Record<string, typeof Database> = {
  excel: FileSpreadsheet,
  sharepoint: List,
  manual_json: Braces,
  sql_server: Database,
  postgresql: Database,
  rest_api: Globe,
};

const SOURCE_KIND: Record<string, string> = {
  excel: "Excel file",
  sharepoint: "SharePoint list",
  manual_json: "Fixed values",
  sql_server: "SQL Server",
  postgresql: "PostgreSQL",
  rest_api: "Web API",
};

export function DataPage() {
  const { hasRole } = useAuth();
  const canAdd = hasRole("platform_admin");
  const [params, setParams] = useSearchParams();
  // v3.1: "My OneDrive & SharePoint" tab, only when the feature is on for this person's organisation.
  const oneDrive = useOneDriveStatus();
  const showMine = !!oneDrive.data && oneDrive.data.state !== "disabled";
  const [view, setView] = useState<"shared" | "mine">(params.get("onedrive") ? "mine" : "shared");
  const catalog = useQuery({ queryKey: ["admin", "data-catalog"], queryFn: dataHubApi.catalog });
  const [q, setQ] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [wizard, setWizard] = useState<{ open: boolean; file?: CatalogSource }>({ open: false });
  const tableId = params.get("table");
  const sourceId = params.get("source");
  const groupKey = params.get("group");

  const index = useMemo(() => {
    const tables = new Map<string, { table: CatalogTable; source: CatalogSource; group: string }>();
    const sources = new Map<string, { source: CatalogSource; group: string }>();
    for (const g of catalog.data?.groups ?? []) {
      for (const s of g.sources) {
        sources.set(s.id, { source: s, group: g.name });
        for (const t of s.tables) tables.set(t.id, { table: t, source: s, group: g.name });
      }
    }
    return { tables, sources };
  }, [catalog.data]);

  const selectTable = (id: string) => setParams({ table: id }, { replace: true });
  const selectSource = (id: string) => setParams({ source: id }, { replace: true });
  const selectGroup = (key: string) => setParams({ group: key }, { replace: true });

  const totals = useMemo(() => {
    let tables = 0;
    let files = 0;
    for (const g of catalog.data?.groups ?? []) for (const s of g.sources) { files++; tables += s.tables.length; }
    return { sites: catalog.data?.groups.length ?? 0, files, tables };
  }, [catalog.data]);

  const selectedTable = tableId ? index.tables.get(tableId) : undefined;
  const selectedSource = !selectedTable && sourceId ? index.sources.get(sourceId) : undefined;
  const selectedGroup = !selectedTable && !selectedSource && groupKey ? catalog.data?.groups.find((g) => g.key === groupKey) : undefined;

  const viewTabs = showMine ? (
    <div style={{ marginBottom: 16 }}>
      <Tabs<"shared" | "mine"> value={view} onChange={setView} tabs={[{ value: "shared", label: "Shared data", icon: Table2 }, { value: "mine", label: "My OneDrive & SharePoint", icon: Cloud }]} />
    </div>
  ) : null;

  if (showMine && view === "mine") {
    return (
      <div className="ds-page ds-page--wide" style={{ maxWidth: 1640 }}>
        <PageHeader eyebrow="Data" title="Data" subtitle="Use Excel files from your own OneDrive and SharePoint. You only ever see what you already have permission to open." />
        {viewTabs}
        <MyFilesPanel />
      </div>
    );
  }

  return (
    <div className="ds-page ds-page--wide" style={{ maxWidth: 1640 }}>
      <PageHeader
        eyebrow="Data"
        title="Data"
        subtitle="Every table your reports can use, grouped by the SharePoint site it lives in. Add a file or list once — its columns are detected for you, and it's ready in the designer."
        actions={canAdd ? <Button variant="primary" icon={Plus} onClick={() => setWizard({ open: true })}>Add data</Button> : undefined}
      />
      {viewTabs}
      <div className="ds-grid ds-grid--3" style={{ marginBottom: 16 }}>
        <Stat icon={Folder} label="SharePoint sites & groups" value={totals.sites} loading={catalog.isLoading} />
        <Stat icon={FileSpreadsheet} tone="info" label="files & lists" value={totals.files} loading={catalog.isLoading} />
        <Stat icon={Table2} tone="success" label="tables" value={totals.tables} loading={catalog.isLoading} />
      </div>
      {catalog.data && catalog.data.problem_count > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <Banner
            tone="warning"
            title={`${catalog.data.problem_count} table${catalog.data.problem_count > 1 ? "s" : ""} can't be used yet`}
            action={<Button size="sm" onClick={() => setOnlyProblems((v) => !v)}>{onlyProblems ? "Show all" : "Show them"}</Button>}
          >
            They have no columns yet. Open one and press <strong>Detect columns</strong> (for Excel, pick the right sheet first).
          </Banner>
        </div>
      ) : null}
      <div className="ds-split">
        <Card flush>
          <div style={{ padding: 12 }}>
            <SearchInput value={q} onChange={setQ} placeholder="Search tables, files or sites" width="100%" />
          </div>
          {catalog.isLoading ? <SkeletonRows rows={10} /> : catalog.isError ? (
            <div style={{ padding: 12 }}><Banner tone="danger" title="Couldn't load your data">{catalog.error instanceof Error ? catalog.error.message : null}</Banner></div>
          ) : (
            <CatalogTree catalog={catalog.data!} q={q} onlyProblems={onlyProblems} selectedTable={tableId} selectedSource={selectedSource?.source.id ?? null} selectedGroup={selectedGroup?.key ?? null} onTable={selectTable} onSource={selectSource} onGroup={selectGroup} />
          )}
        </Card>
        {selectedTable ? (
          <TableDetail key={selectedTable.table.id} table={selectedTable.table} source={selectedTable.source} group={selectedTable.group} canAdd={canAdd} onRemoved={() => setParams({}, { replace: true })} />
        ) : selectedGroup ? (
          <GroupDetail key={selectedGroup.key} group={selectedGroup} canEdit={canAdd} onSource={selectSource} />
        ) : selectedSource ? (
          <SourceDetail key={selectedSource.source.id} source={selectedSource.source} group={selectedSource.group} canAdd={canAdd} onTable={selectTable} onAddSheets={() => setWizard({ open: true, file: selectedSource.source })} onRemoved={() => setParams({}, { replace: true })} />
        ) : (
          <Card>
            <EmptyState
              icon={Table2}
              title="Pick a table"
              description="See its columns, measures and real rows. Tables are what charts are built from — one Excel sheet or one SharePoint list each."
              action={canAdd ? <Button variant="primary" icon={Plus} onClick={() => setWizard({ open: true })}>Add data</Button> : undefined}
            />
          </Card>
        )}
      </div>
      {canAdd ? (
        <AddDataWizard
          open={wizard.open}
          file={wizard.file}
          catalog={catalog.data}
          onClose={() => setWizard({ open: false })}
          onOpenTable={(id) => { setWizard({ open: false }); selectTable(id); }}
        />
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- tree ---- */

function CatalogTree({
  catalog, q, onlyProblems, selectedTable, selectedSource, selectedGroup, onTable, onSource, onGroup,
}: {
  catalog: Catalog; q: string; onlyProblems: boolean; selectedTable: string | null; selectedSource: string | null; selectedGroup: string | null;
  onTable: (id: string) => void; onSource: (id: string) => void; onGroup: (key: string) => void;
}) {
  // Sites and files start collapsed; the arrow opens them. Until clicked, the one
  // holding the selected item opens by itself so a ?source= / ?table= link shows it.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (key: string, open: boolean) => setExpanded((c) => ({ ...c, [key]: !open }));
  const needle = q.trim().toLowerCase();
  const groups = catalog.groups
    .map((g) => ({
      ...g,
      sources: g.sources
        .map((s) => {
          const sourceHit = !needle || `${g.name} ${s.label} ${s.name}`.toLowerCase().includes(needle);
          const tables = s.tables.filter((t) => (!onlyProblems || t.problem) && (sourceHit || t.name.toLowerCase().includes(needle) || (t.sheet ?? "").toLowerCase().includes(needle)));
          return { ...s, tables, keep: (sourceHit && !onlyProblems) || tables.length > 0 };
        })
        .filter((s) => s.keep),
    }))
    .filter((g) => g.sources.length > 0);

  if (groups.length === 0) {
    return <EmptyState icon={Cloud} title={needle || onlyProblems ? "Nothing matches" : "No data yet"} description={needle ? "Try another word." : "Add an Excel file or a SharePoint list to start."} />;
  }
  return (
    <div style={{ maxHeight: "calc(100vh - 300px)", overflow: "auto", paddingBottom: 8 }}>
      {groups.map((g) => {
        const holdsSelection = g.sources.some((s) => s.id === selectedSource || s.tables.some((t) => t.id === selectedTable));
        const open = needle || onlyProblems ? true : (expanded[`g:${g.name}`] ?? holdsSelection);
        const count = g.sources.reduce((n, s) => n + s.tables.length, 0);
        return (
          <div key={g.name}>
            <div
              className="ds-list__row is-clickable"
              style={{ background: selectedGroup === g.key ? "var(--accent-soft)" : "var(--surface-2, var(--surface))", borderTop: "1px solid var(--divider)", position: "sticky", top: 0, zIndex: 1 }}
              onClick={() => onGroup(g.key)}
              title="Who can use this site's tables"
            >
              <button
                type="button"
                className="st-mini"
                style={{ border: 0, background: "transparent", color: "inherit", padding: 0, display: "inline-flex", cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); toggle(`g:${g.name}`, open); }}
                aria-expanded={open}
                aria-label={open ? `Collapse ${g.name}` : `Expand ${g.name}`}
              >
                {open ? <ChevronDown size={15} className="ds-faint" /> : <ChevronRight size={15} className="ds-faint" />}
              </button>
              <Folder size={15} color="var(--warning)" />
              <div className="ds-list__main"><div className="ds-list__title ds-truncate">{g.name}</div></div>
              {g.grants.length ? <Users size={14} className="ds-faint" aria-label="Shared with report owners" /> : null}
              <span className="ds-small ds-faint">{count}</span>
            </div>
            {open ? g.sources.map((s) => {
              const Icon = SOURCE_ICON[s.type] ?? Database;
              const srcOpen = needle || onlyProblems ? true : (expanded[`s:${s.id}`] ?? s.tables.some((t) => t.id === selectedTable));
              return (
                <div key={s.id}>
                  <div
                    className="ds-list__row is-clickable"
                    style={{ paddingLeft: 14, ...(selectedSource === s.id ? { background: "var(--accent-soft)" } : {}) }}
                    onClick={() => onSource(s.id)}
                    title={s.name}
                  >
                    <button
                      type="button"
                      className="st-mini"
                      style={{ border: 0, background: "transparent", color: "inherit", padding: 0, display: "inline-flex", cursor: "pointer", visibility: s.tables.length ? "visible" : "hidden" }}
                      onClick={(e) => { e.stopPropagation(); toggle(`s:${s.id}`, srcOpen); }}
                      aria-expanded={srcOpen}
                      aria-label={srcOpen ? `Collapse ${s.label}` : `Expand ${s.label}`}
                    >
                      {srcOpen ? <ChevronDown size={14} className="ds-faint" /> : <ChevronRight size={14} className="ds-faint" />}
                    </button>
                    <Icon size={15} className="ds-faint" />
                    <div className="ds-list__main"><div className="ds-truncate ds-small" style={{ fontWeight: 600 }}>{s.label}</div></div>
                    {s.tables.length === 0 ? <Badge>no tables</Badge> : <span className="ds-small ds-faint">{s.tables.length}</span>}
                  </div>
                  {srcOpen ? s.tables.map((t) => (
                    <div
                      key={t.id}
                      className="ds-list__row is-clickable"
                      style={{ paddingLeft: 52, paddingTop: 7, paddingBottom: 7, ...(selectedTable === t.id ? { background: "var(--accent-soft)" } : {}) }}
                      onClick={() => onTable(t.id)}
                    >
                      <Table2 size={14} className="ds-faint" />
                      <div className="ds-list__main">
                        <div className="ds-truncate" style={{ fontSize: 13.5 }}>{t.name}</div>
                        <div className="ds-list__meta ds-truncate" style={{ fontSize: 12 }}>
                          {t.problem ? "no columns yet" : `${t.field_count} column${t.field_count === 1 ? "" : "s"}`}
                          {t.used_by.length ? ` · in ${t.used_by.length} report${t.used_by.length === 1 ? "" : "s"}` : ""}
                        </div>
                      </div>
                      {t.problem ? <AlertTriangle size={15} color="var(--warning)" aria-label="Needs attention" /> : !t.is_active ? <Badge>Off</Badge> : t.last_refresh?.status === "failed" ? <Badge tone="danger">Failed</Badge> : null}
                    </div>
                  )) : null}
                </div>
              );
            }) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- details ---- */

type Tab = "columns" | "measures" | "data" | "clean" | "settings";

function TableDetail({ table, source, group, canAdd, onRemoved }: { table: CatalogTable; source: CatalogSource; group: string; canAdd: boolean; onRemoved: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState<Tab>(table.problem ? "settings" : "columns");
  const fields = useQuery({ queryKey: ["admin", "fields", table.id], queryFn: () => datasetApi.listFields(table.id) });
  const measures = useQuery({ queryKey: ["admin", "measures", table.id], queryFn: () => datasetApi.listMeasures(table.id) });
  const ds = useMemo<Dataset>(() => ({
    id: table.id, name: table.name, description: table.description, data_source_id: source.id, source_object: table.sheet ?? table.name,
    source_sheet: table.sheet, header_row: table.header_row, cache_ttl_seconds: 300, is_active: table.is_active,
    created_at: table.created_at ?? "", updated_at: table.created_at ?? "", group_name: group, source_name: source.label,
  }), [table, source, group]);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "data-catalog"] });
    qc.invalidateQueries({ queryKey: ["admin", "fields", table.id] });
    qc.invalidateQueries({ queryKey: ["admin", "datasets"] });
  };

  const detect = useMutation({
    mutationFn: () => dataHubApi.detectColumns(table.id),
    onSuccess: (r) => {
      invalidate();
      const parts = [r.added.length ? `${r.added.length} new` : null, r.removed.length ? `${r.removed.length} removed` : null, r.blocked.length ? `${r.blocked.length} kept because a chart uses them` : null].filter(Boolean);
      toast.success("Columns up to date", parts.length ? parts.join(" · ") : `No changes — ${r.kept.length} columns.`);
      if (r.added.length) setTab("columns");
    },
    onError: (e) => toast.error("Couldn't read the columns", e instanceof Error ? e.message : undefined),
  });
  const refresh = useMutation({
    mutationFn: () => datasetApi.refresh(table.id),
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["admin", "refresh-runs"] });
      const added = (r as { columns_added?: number }).columns_added ?? 0;
      if (r.error) toast.error("Refresh problem", r.error);
      else toast.success("Refreshed", added ? `${added} columns detected.` : "Fresh data will be used on the next view.");
    },
    onError: (e) => toast.error("Refresh failed", e instanceof Error ? e.message : undefined),
  });
  const remove = useMutation({
    mutationFn: () => dataHubApi.deleteTable(table.id),
    onSuccess: () => { toast.success("Table removed", table.name); qc.invalidateQueries({ queryKey: ["admin", "data-catalog"] }); qc.invalidateQueries({ queryKey: ["admin", "datasets"] }); onRemoved(); },
    onError: (e) => toast.error("Can't remove it", e instanceof Error ? e.message : undefined),
  });
  const askRemove = async () => {
    if (table.used_by.length) {
      toast.error("Still in use", `Used by ${table.used_by.map((r) => r.name).join(", ")}. Remove it from those reports first.`);
      return;
    }
    if (await confirm({ title: `Remove “${table.name}”?`, message: "The table disappears from the designer. The file or list in SharePoint is not touched, and you can add the sheet again any time.", confirmLabel: "Remove table", tone: "danger" })) remove.mutate();
  };

  const Icon = SOURCE_ICON[source.type] ?? Database;
  return (
    <Card
      title={table.name}
      subtitle={<span className="ds-row" style={{ gap: 6, flexWrap: "wrap" }}><Folder size={13} /> {group} <ChevronRight size={12} className="ds-faint" /> <Icon size={13} /> {source.label}{table.sheet ? <><ChevronRight size={12} className="ds-faint" /> sheet “{table.sheet}”</> : null}</span>}
      actions={
        <>
          <Button icon={Wand2} loading={detect.isPending} onClick={() => detect.mutate()} title="Read the column list again from the source">Detect columns</Button>
          <Button icon={RefreshCw} loading={refresh.isPending} onClick={() => refresh.mutate()}>Refresh</Button>
          <Menu
            trigger={(p) => <Button {...p} variant="ghost" icon={MoreHorizontal} aria-label="More" />}
            items={[{ label: "Remove table", icon: Trash2, danger: true, onSelect: () => void askRemove() }]}
          />
        </>
      }
    >
      {table.problem === "no_sheet" ? (
        <div style={{ marginBottom: 14 }}>
          <Banner tone="warning" title="Choose which sheet this table reads">
            No sheet was chosen when this table was made, so it can't find its columns. Pick the sheet below under Settings, then its columns are detected automatically.
          </Banner>
        </div>
      ) : table.problem === "no_columns" ? (
        <div style={{ marginBottom: 14 }}>
          <Banner tone="warning" title="No columns yet" action={<Button size="sm" variant="primary" icon={Wand2} loading={detect.isPending} onClick={() => detect.mutate()}>Detect columns</Button>}>
            The platform hasn't read this table's columns yet. It takes a few seconds.
          </Banner>
        </div>
      ) : !table.sheet_pinned ? (
        <div style={{ marginBottom: 14 }}>
          <Banner tone="info" title="Sheet not pinned">
            This table reads whichever sheet was open when the file was last saved. That's fine for a one-sheet file; for a file with several sheets, pin the right one under Settings.
          </Banner>
        </div>
      ) : null}
      <div className="ds-row" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Badge tone="info">{fields.data?.length ?? table.field_count} columns</Badge>
        <Badge tone="success">{measures.data?.length ?? table.measure_count} measures</Badge>
        {table.step_count ? <Badge tone="info">{table.step_count} clean-up step{table.step_count === 1 ? "" : "s"}</Badge> : null}
        <Badge>{table.last_refresh ? `refreshed ${formatRelative(table.last_refresh.at)} · ${table.last_refresh.status}` : "not refreshed yet"}</Badge>
        {table.used_by.length ? (
          <span className="ds-small ds-muted">Used in {table.used_by.map((r, i) => <span key={r.id}>{i ? ", " : ""}<Link to={`/admin/reports/${r.id}`}>{r.name}</Link></span>)}</span>
        ) : <span className="ds-small ds-faint">Not used in any report yet</span>}
      </div>
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[{ value: "columns", label: "Columns", icon: TypeIcon }, { value: "measures", label: "Measures", icon: Sigma }, { value: "data", label: "Data", icon: Table2 }, { value: "clean", label: "Clean-up", icon: Wand2, count: table.step_count || undefined }, { value: "settings", label: "Settings", icon: Settings2 }]} />
      <div style={{ paddingTop: 14 }}>
        {tab === "columns" ? <FieldsTab ds={ds} fields={fields.data} loading={fields.isLoading} /> : null}
        {tab === "measures" ? <MeasuresTab ds={ds} fields={fields.data ?? []} measures={measures.data} loading={measures.isLoading} /> : null}
        {tab === "data" ? <PreviewTab ds={ds} fields={fields.data ?? []} measures={measures.data ?? []} /> : null}
        {tab === "clean" ? <TableCleanup table={table} canEdit={canAdd} onApplied={() => { invalidate(); qc.invalidateQueries({ queryKey: ["admin", "measures", table.id] }); qc.invalidateQueries({ queryKey: ["studio-fields"] }); qc.invalidateQueries({ queryKey: ["studio-measures"] }); }} /> : null}
        {tab === "settings" ? <TableSettings table={table} source={source} canEdit={canAdd} onSaved={() => { invalidate(); detect.mutate(); }} /> : null}
      </div>
    </Card>
  );
}

function TableSettings({ table, source, canEdit, onSaved }: { table: CatalogTable; source: CatalogSource; canEdit: boolean; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(table.name);
  const [sheet, setSheet] = useState(table.sheet ?? "");
  const [header, setHeader] = useState(String(table.header_row ?? 1));
  const isExcel = source.type === "excel";
  const sheets = useQuery({ queryKey: ["admin", "source-sheets", source.id], queryFn: () => dataHubApi.sourceSheets(source.id), enabled: isExcel && canEdit, retry: false, staleTime: 60_000 });
  const worksheets = (sheets.data ?? []).filter((o) => o.type === "worksheet");
  const excelTables = (sheets.data ?? []).filter((o) => o.type === "table") as Array<{ name: string; worksheet?: string; source_sheet?: string; row_count?: number }>;
  const isTableTarget = sheet.startsWith("table:");
  const dirty = name.trim() !== table.name || (isExcel && (sheet !== (table.sheet ?? "") || Number(header) !== (table.header_row ?? 1)));
  const sheetChanged = isExcel && (sheet !== (table.sheet ?? "") || Number(header) !== (table.header_row ?? 1));
  const save = useMutation({
    mutationFn: () => dataHubApi.updateTable(table.id, {
      ...(name.trim() !== table.name ? { name: name.trim() } : {}),
      // The catalog shows header rows 1-based; the table stores rows skipped above it.
      ...(sheetChanged ? { source_sheet: sheet, source_object: sheet, header_row: Math.max(0, (Number(header) || 1) - 1) } : {}),
    }),
    onSuccess: () => { toast.success("Saved", sheetChanged ? "Reading the columns of the new sheet…" : undefined); if (sheetChanged) onSaved(); },
    onError: (e) => toast.error("Couldn't save", e instanceof Error ? e.message : undefined),
  });
  return (
    <div className="ds-stack" style={{ maxWidth: 640 }}>
      <Field label="Table name" hint="How it appears in the designer."><Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} /></Field>
      {isExcel ? (
        <>
          <Field label="Sheet" hint={sheets.isLoading ? "Reading the workbook…" : sheets.isError ? "Couldn't list the sheets of this file." : "The Excel tab this table reads."}>
            <Select value={sheet} onChange={(e) => setSheet(e.target.value)} disabled={!canEdit || sheets.isLoading}>
              {!sheet ? <option value="" disabled>Choose a sheet…</option> : null}
              {sheet && !worksheets.some((w) => w.name === sheet) && !excelTables.some((t) => (t.source_sheet ?? `table:${t.name}`) === sheet) ? <option value={sheet}>{sheet}</option> : null}
              {excelTables.length ? (
                <optgroup label="Excel tables">
                  {excelTables.map((t) => <option key={t.name} value={t.source_sheet ?? `table:${t.name}`}>{t.name}{t.worksheet ? ` — on “${t.worksheet}”` : ""}{t.row_count ? ` · ${t.row_count.toLocaleString()} rows` : ""}</option>)}
                </optgroup>
              ) : null}
              <optgroup label="Sheets">
                {worksheets.map((w) => (
                  <option key={w.name} value={w.name}>{w.name}{w.hidden ? " (hidden)" : ""}{w.first_columns?.length ? ` — ${w.first_columns.slice(0, 3).join(", ")}` : ""}</option>
                ))}
              </optgroup>
            </Select>
          </Field>
          <Field label="Header row" hint={isTableTarget ? "An Excel table's first row is always its header." : "The row that holds the column names (1 = the first row)."}>
            <Select value={isTableTarget ? "1" : header} onChange={(e) => setHeader(e.target.value)} disabled={!canEdit || isTableTarget} style={{ width: 120 }}>
              {Array.from({ length: 15 }, (_, i) => String(i + 1)).map((n) => <option key={n} value={n}>Row {n}</option>)}
            </Select>
          </Field>
        </>
      ) : null}
      <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "180px 1fr", gap: "8px 16px" }} className="ds-small">
        <dt className="ds-muted">Comes from</dt><dd style={{ margin: 0 }}>{SOURCE_KIND[source.type] ?? source.type} · {source.label}{source.folder_path ? ` (${source.folder_path})` : ""}</dd>
        <dt className="ds-muted">Cache</dt><dd style={{ margin: 0 }}>Keeps results for 5 min, then refreshes in the background</dd>
        <dt className="ds-muted">Added</dt><dd style={{ margin: 0 }}>{formatRelative(table.created_at)}</dd>
      </dl>
      {canEdit ? (
        <div className="ds-row" style={{ justifyContent: "flex-end" }}>
          <Button variant="primary" disabled={!dirty || (isExcel && !sheet && !table.sheet)} loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </div>
      ) : null}
    </div>
  );
}

/** Power Query-style "Applied steps" for one table: what it has now, what
 * the Navigator would do today, switch steps on/off, apply. Columns a report
 * still uses are kept (the result says which). */
function TableCleanup({ table, canEdit, onApplied }: { table: CatalogTable; canEdit: boolean; onApplied: () => void }) {
  const toast = useToast();
  const info = useQuery({ queryKey: ["admin", "cleanup", table.id], queryFn: () => dataHubApi.cleanup(table.id), retry: false, staleTime: 60_000 });
  const [steps, setSteps] = useState<CleanStep[] | null>(null);
  const [header, setHeader] = useState<number | null>(null);
  const isTable = (table.sheet ?? "").startsWith("table:");
  useEffect(() => {
    if (info.data && steps === null) {
      setSteps(info.data.current.steps.map((s) => ({ ...s, label: s.label ?? (s.config?.label as string | undefined) ?? null })));
      setHeader(info.data.current.header_row);
    }
  }, [info.data, steps]);
  const suggested = info.data?.suggested;
  const sameAsSuggested = !!suggested && JSON.stringify((suggested.steps ?? []).map((s) => [s.type, s.config])) === JSON.stringify((steps ?? []).filter((s) => s.is_active !== false).map((s) => [s.type, Object.fromEntries(Object.entries(s.config ?? {}).filter(([k]) => k !== "label"))])) && (isTable || suggested.header_row === header);
  const dirty = !!info.data && (JSON.stringify(steps) !== JSON.stringify(info.data.current.steps.map((s) => ({ ...s, label: s.label ?? (s.config?.label as string | undefined) ?? null }))) || header !== info.data.current.header_row);
  const apply = useMutation({
    mutationFn: () => dataHubApi.applyCleanup(table.id, { header_row: isTable ? null : header, steps: steps ?? [], auto_measures: true }),
    onSuccess: (r) => {
      const parts = [r.added.length ? `${r.added.length} new column${r.added.length === 1 ? "" : "s"}` : null, r.removed.length ? `${r.removed.length} removed` : null, r.blocked.length ? `${r.blocked.length} kept because a report uses them` : null, r.measures_created.length ? `${r.measures_created.length} measures added` : null].filter(Boolean);
      toast.success("Clean-up applied", parts.join(" · ") || "Columns unchanged.");
      info.refetch().then(() => setSteps(null));
      onApplied();
    },
    onError: (e) => toast.error("Couldn't apply the clean-up", e instanceof Error ? e.message : undefined),
  });

  if (info.isLoading || steps === null) return info.isError ? <Banner tone="danger" title="Couldn't read this table's source">{info.error instanceof Error ? info.error.message : null}</Banner> : <SkeletonRows rows={4} />;
  return (
    <div className="ds-stack" style={{ maxWidth: 1100 }}>
      {table.used_by.length ? (
        <Banner tone="info" title={`Used by ${table.used_by.length} report${table.used_by.length === 1 ? "" : "s"}`}>Columns those reports use are always kept. Check the report after applying a clean-up that unpivots or renames columns.</Banner>
      ) : null}
      <div className="ds-row" style={{ gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "0 0 360px" }} className="ds-stack">
          <div style={{ fontWeight: 650 }}>Applied steps</div>
          <div className="ds-small ds-muted">{isTable ? `Source · Excel table “${(table.sheet ?? "").slice(6)}”` : `Source · ${table.sheet ? `sheet “${table.sheet}”` : "the file's first sheet"}`}</div>
          {!isTable ? (
            <div className="ds-row" style={{ gap: 8 }}>
              <span className="ds-small">Column names on row</span>
              <Select inputSize="sm" value={String(header ?? 1)} onChange={(e) => setHeader(Number(e.target.value))} disabled={!canEdit} style={{ width: 90 }}>
                {Array.from({ length: 40 }, (_, i) => String(i + 1)).map((n) => <option key={n} value={n}>{n}</option>)}
              </Select>
            </div>
          ) : null}
          {steps.length === 0 ? <div className="ds-small ds-faint">No clean-up steps yet.</div> : null}
          {steps.map((s, i) => (
            <div key={i} className="ds-row" style={{ gap: 6 }}>
              <Switch checked={s.is_active !== false} disabled={!canEdit} label={<span className="ds-small">{s.label ?? s.type.replace(/_/g, " ")}</span>} onChange={(v) => setSteps(steps.map((x, j) => (j === i ? { ...x, is_active: v } : x)))} />
              <div className="ds-grow" />
              {canEdit ? <button type="button" className="st-mini" aria-label="Remove step" onClick={() => setSteps(steps.filter((_, j) => j !== i))} style={{ border: 0, background: "transparent", cursor: "pointer", color: "var(--text-muted)" }}><X size={13} /></button> : null}
            </div>
          ))}
          {canEdit ? (
            <div className="ds-row" style={{ gap: 8, marginTop: 6 }}>
              <Button variant="primary" icon={CheckCircle2} disabled={!dirty} loading={apply.isPending} onClick={() => apply.mutate()}>Apply</Button>
              <Button disabled={!dirty} onClick={() => { setSteps(null); void info.refetch(); }}>Discard</Button>
            </div>
          ) : null}
        </div>
        <div style={{ flex: 1, minWidth: 360 }} className="ds-stack">
          {suggested && !sameAsSuggested ? (
            <>
              <div className="ds-row" style={{ gap: 8 }}>
                <Wand2 size={16} color="var(--accent)" />
                <div style={{ fontWeight: 650 }}>Suggested clean-up</div>
                <div className="ds-grow" />
                {canEdit ? <Button size="sm" onClick={() => { setSteps(suggested.steps.map((s) => ({ ...s, is_active: true }))); setHeader(suggested.header_row); }}>Use these steps</Button> : null}
              </div>
              <div className="ds-small ds-muted">
                {suggested.reason ? `${suggested.reason}. ` : ""}Column names on row {suggested.header_row}{suggested.steps.length ? ` · ${suggested.steps.map((s) => s.label ?? s.type).join(" · ")}` : " · nothing else to fix"}
              </div>
              <CleanedRows columns={suggested.columns ?? []} rows={suggested.sample ?? []} />
            </>
          ) : (
            <Banner tone="success" title="Looks clean">Nothing more to fix — the Navigator would do the same today.</Banner>
          )}
        </div>
      </div>
    </div>
  );
}

function SourceDetail({ source, group, canAdd, onTable, onAddSheets, onRemoved }: { source: CatalogSource; group: string; canAdd: boolean; onTable: (id: string) => void; onAddSheets: () => void; onRemoved: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const Icon = SOURCE_ICON[source.type] ?? Database;
  const test = useMutation({
    mutationFn: () => dataSourceApi.test(source.id),
    onSuccess: (r) => (r.status === "ok" || r.status === "success" ? toast.success("Connection works", source.label) : toast.error("Connection problem", r.message ?? r.status)),
    onError: (e) => toast.error(`Couldn't reach ${source.label}`, e instanceof Error ? e.message : undefined),
  });
  const remove = useMutation({
    mutationFn: () => dataHubApi.deleteSource(source.id),
    onSuccess: () => { toast.success("Removed", source.label); qc.invalidateQueries({ queryKey: ["admin", "data-catalog"] }); onRemoved(); },
    onError: (e) => toast.error("Can't remove it", e instanceof Error ? e.message : undefined),
  });
  return (
    <Card
      title={source.label}
      subtitle={<span className="ds-row" style={{ gap: 6 }}><Folder size={13} /> {group} · <Icon size={13} /> {SOURCE_KIND[source.type] ?? source.type}{source.folder_path ? ` · ${source.folder_path}` : ""}</span>}
      actions={
        <>
          {canAdd && source.type === "excel" && source.locator.drive_id ? <Button variant="primary" icon={Plus} onClick={onAddSheets}>Add tables from this file</Button> : null}
          {canAdd ? <Button icon={Plug} loading={test.isPending} onClick={() => test.mutate()}>Test connection</Button> : null}
          {canAdd ? (
            <Menu
              trigger={(p) => <Button {...p} variant="ghost" icon={MoreHorizontal} aria-label="More" />}
              items={[{ label: "Remove from the platform", icon: Trash2, danger: true, disabled: source.tables.length > 0, onSelect: async () => { if (await confirm({ title: `Remove “${source.label}”?`, message: "Only the connection is removed — the file stays in SharePoint.", confirmLabel: "Remove", tone: "danger" })) remove.mutate(); } }]}
            />
          ) : null}
        </>
      }
    >
      {source.description ? <div className="ds-small ds-muted" style={{ marginTop: -6, marginBottom: 12 }}>{source.description}</div> : null}
      {source.tables.length === 0 ? (
        <EmptyState icon={Table2} title="No tables from this file yet" description={source.type === "excel" ? "Pick the sheets you want to use — their columns are detected for you." : "Nothing reads from this source yet."} action={canAdd && source.type === "excel" && source.locator.drive_id ? <Button variant="primary" icon={Plus} onClick={onAddSheets}>Add sheets</Button> : undefined} />
      ) : (
        <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
          {source.tables.map((t) => (
            <div key={t.id} className="ds-list__row is-clickable" onClick={() => onTable(t.id)}>
              <Table2 size={16} className="ds-faint" />
              <div className="ds-list__main">
                <div className="ds-list__title">{t.name}</div>
                <div className="ds-list__meta">{t.sheet ? `sheet “${t.sheet}” · ` : ""}{t.problem ? "no columns yet" : `${t.field_count} columns`}{t.used_by.length ? ` · in ${t.used_by.length} report${t.used_by.length === 1 ? "" : "s"}` : ""}</div>
              </div>
              {t.problem ? <Badge tone="warning">Needs attention</Badge> : null}
              <ChevronRight size={16} className="ds-faint" />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function GroupDetail({ group, canEdit, onSource }: { group: CatalogGroup; canEdit: boolean; onSource: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers, enabled: canEdit, staleTime: 60_000 });
  const [grants, setGrants] = useState<AccessGrant[]>(group.grants);
  const [pick, setPick] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => setGrants(group.grants), [group.grants]);
  const everyOwner = grants.some((g) => g.principal_type === "role" && g.principal_value === "report_owner");
  const people = grants.filter((g) => g.principal_type === "user");
  const dirty = JSON.stringify(grants.map((g) => `${g.principal_type}:${g.principal_value}`).sort()) !== JSON.stringify(group.grants.map((g) => `${g.principal_type}:${g.principal_value}`).sort());
  const nameOf = (id: string) => users.data?.find((u) => u.id === id)?.display_name ?? grants.find((g) => g.principal_value === id)?.label ?? "Person";
  const candidates = (users.data ?? [])
    .filter((u) => u.is_active && !u.roles.some((r) => r === "platform_admin" || r === "report_admin") && !people.some((p) => p.principal_value === u.id))
    .filter((u) => !q || `${u.display_name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const save = useMutation({
    mutationFn: () => dataHubApi.setAccess(group.key, group.name, grants),
    onSuccess: () => { toast.success("Access saved", `${group.name}: ${everyOwner ? "every report owner" : `${people.length} ${people.length === 1 ? "person" : "people"}`}`); qc.invalidateQueries({ queryKey: ["admin", "data-catalog"] }); },
    onError: (e) => toast.error("Couldn't save access", e instanceof Error ? e.message : undefined),
  });
  const tables = group.sources.reduce((n, s) => n + s.tables.length, 0);
  return (
    <Card title={group.name} subtitle={`${group.sources.length} file${group.sources.length === 1 ? "" : "s"} or list${group.sources.length === 1 ? "" : "s"} · ${tables} table${tables === 1 ? "" : "s"}`}>
      <div className="ds-stack">
        <div>
          <div style={{ fontWeight: 650, marginBottom: 4 }}>Who can build reports with these tables</div>
          <div className="ds-small ds-muted">Admins always can. Report owners only see the tables of the sites you share with them (plus tables their own reports already use). Sharing never lets them add or browse SharePoint data.</div>
        </div>
        <Checkbox label="Every report owner" checked={everyOwner} disabled={!canEdit} onChange={(v) => setGrants((g) => (v ? [...g, { principal_type: "role", principal_value: "report_owner", label: "Every report owner" }] : g.filter((x) => !(x.principal_type === "role" && x.principal_value === "report_owner"))))} />
        <div>
          <div className="ds-small ds-muted" style={{ marginBottom: 6 }}>Or only these people</div>
          <div className="ds-row" style={{ flexWrap: "wrap", gap: 6 }}>
            {people.length === 0 ? <span className="ds-small ds-faint">Nobody yet.</span> : null}
            {people.map((p) => (
              <span key={p.principal_value} className="ds-row" style={{ gap: 4, padding: "3px 6px 3px 10px", borderRadius: 999, border: "1px solid var(--border)", fontSize: 13 }}>
                {nameOf(p.principal_value)}
                {canEdit ? <button type="button" className="st-mini" style={{ border: 0, background: "transparent", color: "inherit", cursor: "pointer", display: "inline-flex" }} aria-label={`Remove ${nameOf(p.principal_value)}`} onClick={() => setGrants((g) => g.filter((x) => x !== p))}><X size={13} /></button> : null}
              </span>
            ))}
          </div>
        </div>
        {canEdit ? (
          <div className="ds-row" style={{ gap: 8, flexWrap: "wrap" }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person…" style={{ width: 220 }} aria-label="Find a person" />
            <Select value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 260 }} aria-label="Person">
              <option value="">{users.isLoading ? "Loading people…" : candidates.length ? `Choose (${candidates.length})…` : "No matching people"}</option>
              {candidates.slice(0, 200).map((u) => <option key={u.id} value={u.id}>{u.display_name} — {u.email}</option>)}
            </Select>
            <Button icon={UserPlus} disabled={!pick} onClick={() => { setGrants((g) => [...g, { principal_type: "user", principal_value: pick, label: nameOf(pick) }]); setPick(""); }}>Add</Button>
            <div className="ds-grow" />
            <Button disabled={!dirty} onClick={() => setGrants(group.grants)}>Discard</Button>
            <Button variant="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Save access</Button>
          </div>
        ) : null}
        <div>
          <div className="ds-small ds-muted" style={{ margin: "6px 0" }}>Files and lists in this site</div>
          <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
            {group.sources.map((s) => {
              const Icon = SOURCE_ICON[s.type] ?? Database;
              return (
                <div key={s.id} className="ds-list__row is-clickable" onClick={() => onSource(s.id)}>
                  <Icon size={16} className="ds-faint" />
                  <div className="ds-list__main"><div className="ds-list__title ds-truncate">{s.label}</div><div className="ds-list__meta">{s.tables.length} table{s.tables.length === 1 ? "" : "s"}</div></div>
                  <ChevronRight size={16} className="ds-faint" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------- wizard ---- */

type Step = "site" | "library" | "files" | "sheets" | "list" | "done";

/** One row of the v2.3 Navigator: a sheet or Excel Table, ticked or not, with its clean-up. */
interface Pick { selected: boolean; name: string; header: number; steps: CleanStep[]; dirty: boolean }

const TYPE_ICON: Record<string, typeof Database> = { number: Hash, date: Calendar, datetime: Calendar, boolean: ToggleLeft, string: TypeIcon };

const ROLE_LABEL: Record<CandidateRole, string> = {
  data: "Data table", pivot: "Pivot summary", layout: "Formatted report", notes: "Notes", has_tables: "Holds Excel tables", empty: "Empty",
};

const pickKey = (c: Candidate) => c.key ?? "__list__";

function AddDataWizard({ open, file, catalog, onClose, onOpenTable }: { open: boolean; file?: CatalogSource; catalog?: Catalog; onClose: () => void; onOpenTable: (id: string) => void }) {
  const qc = useQueryClient();
  const tenants = useQuery({ queryKey: ["admin", "tenants"], queryFn: adminApi.listTenants, enabled: open });
  const [tenant, setTenant] = useState("");
  const tenantId = tenant || file?.locator.tenant_id || tenants.data?.find((t) => t.is_active)?.tenant_id || "";
  const [step, setStep] = useState<Step>("site");
  const [query, setQuery] = useState("");
  const [sites, setSites] = useState<SharePointSite[] | null>(null);
  const [site, setSite] = useState<{ id: string; name: string } | null>(null);
  const [drives, setDrives] = useState<SharePointDrive[]>([]);
  const [lists, setLists] = useState<SharePointList[]>([]);
  const [drive, setDrive] = useState<SharePointDrive | null>(null);
  const [path, setPath] = useState<string[]>([]);
  const [items, setItems] = useState<SharePointDriveItem[] | null>(null);
  const [excel, setExcel] = useState<{ driveId: string; itemId: string; name: string; folder: string } | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showOthers, setShowOthers] = useState(false);
  const [autoMeasures, setAutoMeasures] = useState(true);
  const [list, setList] = useState<SharePointList | null>(null);
  const [listName, setListName] = useState("");
  const [result, setResult] = useState<ConnectResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = () => { setStep("site"); setSites(null); setSite(null); setDrive(null); setPath([]); setItems(null); setExcel(null); setCandidates([]); setPicks({}); setExpanded(null); setShowOthers(false); setList(null); setResult(null); setErr(null); setQuery(""); };
  const close = () => { reset(); onClose(); };
  const run = async (fn: () => Promise<void>) => { setBusy(true); setErr(null); try { await fn(); } catch (e) { setErr(e instanceof Error ? e.message : "Something went wrong"); } finally { setBusy(false); } };

  // Files already connected (badge in the file list) and lists already added.
  const existingFiles = useMemo(() => {
    const m = new Set<string>();
    for (const g of catalog?.groups ?? []) for (const s of g.sources) if (s.locator.item_id) m.add(`${s.locator.drive_id}/${s.locator.item_id}`);
    return m;
  }, [catalog]);
  const existingLists = useMemo(() => {
    const m = new Set<string>();
    for (const g of catalog?.groups ?? []) for (const s of g.sources) if (s.locator.list_id && s.tables.length) m.add(s.locator.list_id);
    return m;
  }, [catalog]);

  const specFor = (x: { driveId: string; itemId: string; name: string; folder: string }, siteInfo: { id: string; name: string } | null): SourceSpec => ({
    kind: "excel", tenant_id: tenantId, site_id: siteInfo?.id || file?.locator.site_id, site_name: siteInfo?.name || file?.locator.site_name,
    drive_id: x.driveId, item_id: x.itemId, file_name: x.name, folder_path: x.folder || undefined,
  });

  const openSheets = (x: { driveId: string; itemId: string; name: string; folder: string }, siteInfo: { id: string; name: string }) =>
    run(async () => {
      const found = await dataHubApi.profile(specFor(x, siteInfo));
      setSite(siteInfo);
      setExcel(x);
      setCandidates(found.candidates);
      setPicks(Object.fromEntries(found.candidates.map((c) => [pickKey(c), { selected: c.selected && !c.existing, name: c.name, header: c.header_row, steps: c.steps.map((s) => ({ ...s, is_active: true })), dirty: false }])));
      setExpanded(null);
      setShowOthers(false);
      setStep("sheets");
    });

  // "Add sheets from this file" jumps straight to the Navigator.
  useEffect(() => {
    if (open && file?.locator.drive_id && file.locator.item_id && tenantId && step === "site") {
      void openSheets({ driveId: file.locator.drive_id, itemId: file.locator.item_id, name: file.label, folder: file.folder_path ?? "" }, { id: file.locator.site_id ?? "", name: file.locator.site_name ?? "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file?.id, tenantId]);

  const openFolder = (d: SharePointDrive, p: string[]) => run(async () => { setDrive(d); setPath(p); setItems(await sharePointApi.browseDrive(d.id, p.join("/"), tenantId)); setStep("files"); });

  const excelSpec = (): SourceSpec => specFor(excel!, site);
  const listSpec = (): SourceSpec => ({ kind: "sharepoint_list", tenant_id: tenantId, site_id: site!.id, site_name: site!.name, list_id: list!.id, list_name: list!.name });

  const setPick = (key: string, patch: Partial<Pick>) => setPicks((x) => ({ ...x, [key]: { ...x[key], ...patch } }));
  const chosen = candidates.filter((c) => picks[pickKey(c)]?.selected && !c.existing);
  const dataCands = candidates.filter((c) => c.role === "data");
  const otherCands = candidates.filter((c) => c.role !== "data");
  const allData = dataCands.filter((c) => !c.existing);
  const connect = (spec: SourceSpec, tables: ConnectTable[]) =>
    run(async () => {
      const r = await dataHubApi.connect(spec, tables, autoMeasures);
      setResult(r);
      setStep("done");
      qc.invalidateQueries({ queryKey: ["admin", "data-catalog"] });
      qc.invalidateQueries({ queryKey: ["admin", "datasets"] });
      qc.invalidateQueries({ queryKey: ["admin", "data-sources"] });
      qc.invalidateQueries({ queryKey: ["studio-datasets"] });
    });

  const stepLabel: Record<Step, string> = {
    site: "Step 1 · Find the SharePoint site",
    library: `Step 2 · Pick a library (Excel files) or a list in ${site?.name ?? ""}`,
    files: `Step 3 · Pick the Excel file in ${drive?.name ?? ""}`,
    sheets: `Step 4 · Everything in ${excel?.name ?? ""} — data tables are ticked and cleaned for you`,
    list: `Step 3 · Check the list “${list?.name ?? ""}”`,
    done: "Done",
  };

  const footer = (() => {
    if (step === "library") return <Button icon={ArrowLeft} onClick={() => setStep("site")}>Back</Button>;
    if (step === "files") return <Button icon={ArrowLeft} onClick={() => (path.length ? void openFolder(drive!, path.slice(0, -1)) : setStep("library"))}>Back</Button>;
    if (step === "sheets") return (
      <>
        {!file ? <Button icon={ArrowLeft} onClick={() => setStep(drive ? "files" : "site")}>Back</Button> : null}
        <Switch label="Create totals & averages" checked={autoMeasures} onChange={setAutoMeasures} />
        <div className="ds-grow" />
        <Button variant="primary" icon={CheckCircle2} disabled={!chosen.length || chosen.some((c) => !picks[pickKey(c)].name.trim())} loading={busy}
          onClick={() => void connect(excelSpec(), chosen.map((c) => { const p = picks[pickKey(c)]; return { sheet: c.key, name: p.name.trim(), header_row: c.kind === "table" ? null : p.header, steps: p.steps }; }))}>
          {chosen.length ? `Add ${chosen.length} table${chosen.length === 1 ? "" : "s"}` : "Tick a table to add"}
        </Button>
      </>
    );
    if (step === "list") return (
      <>
        <Button icon={ArrowLeft} onClick={() => setStep("library")}>Back</Button>
        <Switch label="Create totals & averages" checked={autoMeasures} onChange={setAutoMeasures} />
        <div className="ds-grow" />
        <Button variant="primary" icon={CheckCircle2} disabled={!listName.trim()} loading={busy} onClick={() => void connect(listSpec(), [{ name: listName.trim() }])}>Add table</Button>
      </>
    );
    if (step === "done") return <><div className="ds-grow" /><Button onClick={close}>Close</Button></>;
    return undefined;
  })();

  const candidateRow = (c: Candidate) => {
    const key = pickKey(c);
    const p = picks[key];
    if (!p) return null;
    const Icon = c.kind === "table" ? Table2 : FileSpreadsheet;
    const activeSteps = p.steps.filter((s) => s.is_active !== false).length;
    const isOpen = expanded === key;
    return (
      <div key={key} style={{ borderTop: "1px solid var(--divider)" }}>
        <div className="ds-list__row" style={{ borderTop: 0, alignItems: "center" }}>
          <Checkbox label="" checked={!!p.selected} disabled={!!c.existing || c.role === "empty"} onChange={(v) => setPick(key, { selected: v })} />
          <Icon size={16} color={c.kind === "table" ? "var(--accent)" : "var(--success)"} />
          <div className="ds-list__main" style={{ minWidth: 150 }}>
            <div className="ds-list__title ds-truncate" title={c.key ?? undefined}>
              {c.kind === "table" ? c.name : c.sheet}
              {c.kind === "table" ? <span className="ds-faint" style={{ fontWeight: 400 }}> · Excel table on “{c.sheet}”</span> : null}
            </div>
            <div className="ds-list__meta">
              {c.row_count.toLocaleString()} rows · {c.column_count} columns{c.kind === "sheet" && p.header > 1 ? ` · header on row ${p.header}` : ""}
              {c.reason && !c.existing ? <> · <span style={{ color: "var(--text-muted)" }}>{c.reason}</span></> : null}
            </div>
          </div>
          {c.existing ? <Badge tone="success">already added</Badge> : (
            <>
              {c.role !== "data" ? <Badge tone="neutral">{ROLE_LABEL[c.role]}</Badge> : null}
              <Input inputSize="sm" value={p.name} onChange={(e) => setPick(key, { name: e.target.value, selected: true })} style={{ width: 220 }} aria-label="Table name" />
              <Button size="sm" variant="ghost" icon={isOpen ? ChevronDown : Wand2} onClick={() => setExpanded(isOpen ? null : key)} title="See the clean-up and the cleaned rows">
                {activeSteps ? `${activeSteps} clean-up step${activeSteps === 1 ? "" : "s"}` : "Check"}
              </Button>
            </>
          )}
        </div>
        {isOpen && !c.existing ? (
          <CandidateDetail
            spec={excelSpec()}
            c={c}
            pick={p}
            onChange={(patch) => setPick(key, { ...patch, dirty: true, selected: true })}
          />
        ) : null}
      </div>
    );
  };

  return (
    <Modal open={open} onClose={close} size="xl" title={file ? `Add tables from ${file.label}` : "Add data"} description={stepLabel[step]} footer={footer}>
      {err ? <div style={{ marginBottom: 12 }}><Banner tone="danger" title="That didn't work">{err}</Banner></div> : null}
      {busy && (step === "site" && file) ? <div className="ds-stack"><div className="ds-small ds-muted">Reading every sheet and table in the file…</div><SkeletonRows rows={4} /></div> : null}

      {step === "site" && !file ? (
        <div className="ds-stack">
          {(tenants.data ?? []).filter((t) => t.is_active).length > 1 ? (
            <Field label="Organization">
              <Select value={tenantId} onChange={(e) => setTenant(e.target.value)}>
                {(tenants.data ?? []).filter((t) => t.is_active).map((t) => <option key={t.id} value={t.tenant_id}>{t.display_name}</option>)}
              </Select>
            </Field>
          ) : null}
          <div className="ds-row">
            <div className="ds-input-wrap ds-grow"><Search size={15} /><input className="ds-input" placeholder="Site name, e.g. Insurance or MOP - HR" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void run(async () => setSites(await sharePointApi.searchSites(query, tenantId)))} autoFocus /></div>
            <Button variant="primary" loading={busy} onClick={() => void run(async () => setSites(await sharePointApi.searchSites(query, tenantId)))}>Search</Button>
          </div>
          {(catalog?.groups ?? []).some((g) => g.site_id) && sites === null ? (
            <div>
              <div className="ds-small ds-muted" style={{ marginBottom: 6 }}>Sites you already use</div>
              <div className="ds-row" style={{ flexWrap: "wrap", gap: 6 }}>
                {(catalog?.groups ?? []).filter((g) => g.site_id).map((g) => (
                  <Button key={g.name} size="sm" icon={Folder} onClick={() => void run(async () => { const s = { id: g.site_id!, name: g.name }; setSite(s); const [d, l] = await Promise.all([sharePointApi.listDrives(s.id, tenantId), sharePointApi.listLists(s.id, tenantId)]); setDrives(d); setLists(l.filter((x) => !x.isDocumentLibrary)); setStep("library"); })}>{g.name}</Button>
                ))}
              </div>
            </div>
          ) : null}
          {sites === null ? <div className="ds-small ds-muted">Or type part of any site name and press Enter.</div> : sites.length === 0 ? <div className="ds-small ds-muted">No sites found.</div> : (
            <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10, maxHeight: 360, overflow: "auto" }}>
              {sites.map((s) => (
                <div key={s.id} className="ds-list__row is-clickable" onClick={() => void run(async () => { setSite(s); const [d, l] = await Promise.all([sharePointApi.listDrives(s.id, tenantId), sharePointApi.listLists(s.id, tenantId)]); setDrives(d); setLists(l.filter((x) => !x.isDocumentLibrary)); setStep("library"); })}>
                  <Globe size={16} className="ds-faint" />
                  <div className="ds-list__main"><div className="ds-list__title">{s.name}</div><div className="ds-list__meta ds-truncate">{s.webUrl}</div></div>
                  <ChevronRight size={16} className="ds-faint" />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {step === "library" ? (
        <div className="ds-grid ds-grid--2">
          <Card title="Document libraries" subtitle="Excel files live here" flush>
            <div className="ds-list">
              {drives.map((d) => (
                <div key={d.id} className="ds-list__row is-clickable" onClick={() => void openFolder(d, [])}>
                  <Folder size={16} color="var(--warning)" />
                  <div className="ds-list__main"><div className="ds-list__title">{d.name}</div></div>
                  <ChevronRight size={16} className="ds-faint" />
                </div>
              ))}
              {drives.length === 0 ? <div className="ds-list__row ds-muted">None</div> : null}
            </div>
          </Card>
          <Card title="Lists" subtitle="A SharePoint list becomes one table" flush>
            <div className="ds-list" style={{ maxHeight: 420, overflow: "auto" }}>
              {lists.map((l) => (
                <div key={l.id} className="ds-list__row is-clickable" onClick={() => { setList(l); setListName(l.name); setStep("list"); }}>
                  <List size={16} className="ds-faint" />
                  <div className="ds-list__main"><div className="ds-list__title">{l.name}</div></div>
                  {existingLists.has(l.id) ? <Badge tone="success">added</Badge> : <ChevronRight size={16} className="ds-faint" />}
                </div>
              ))}
              {lists.length === 0 ? <div className="ds-list__row ds-muted">No lists</div> : null}
            </div>
          </Card>
        </div>
      ) : null}

      {step === "files" ? (
        <div className="ds-stack">
          <div className="ds-small ds-muted">{drive?.name}{path.length ? ` / ${path.join(" / ")}` : ""}</div>
          {busy ? <div className="ds-small ds-muted">Reading every sheet and table in the file…</div> : null}
          <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10, maxHeight: 420, overflow: "auto" }}>
            {(items ?? []).filter((i) => i.isFolder || i.isExcel).map((i) => (
              <div key={i.id} className="ds-list__row is-clickable" onClick={() => void (i.isFolder ? openFolder(drive!, [...path, i.name]) : openSheets({ driveId: drive!.id, itemId: i.id, name: i.name, folder: [drive!.name, ...path].join(" / ") }, site!))}>
                {i.isFolder ? <Folder size={16} color="var(--warning)" /> : <FileSpreadsheet size={16} color="var(--success)" />}
                <div className="ds-list__main"><div className="ds-list__title">{i.name}</div></div>
                {!i.isFolder && existingFiles.has(`${drive!.id}/${i.id}`) ? <Badge tone="success">connected</Badge> : null}
                <ChevronRight size={16} className="ds-faint" />
              </div>
            ))}
            {items && items.filter((i) => i.isFolder || i.isExcel).length === 0 ? <div className="ds-list__row ds-muted">No Excel files here.</div> : null}
          </div>
        </div>
      ) : null}

      {step === "sheets" && excel ? (
        <div className="ds-stack">
          <div className="ds-row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Badge tone="info">{dataCands.length} data table{dataCands.length === 1 ? "" : "s"}</Badge>
            {otherCands.length ? <Badge>{otherCands.length} other sheet{otherCands.length === 1 ? "" : "s"}</Badge> : null}
            <span className="ds-small ds-muted">Each ticked table is added with its header found, empty columns removed, text numbers and dates fixed and wide year/month columns unpivoted — like Power Query, already done.</span>
            <div className="ds-grow" />
            {allData.length ? <Button size="sm" onClick={() => setPicks((x) => { const n = { ...x }; for (const c of allData) n[pickKey(c)] = { ...n[pickKey(c)], selected: true }; return n; })}>Tick all data tables</Button> : null}
            <Button size="sm" variant="ghost" onClick={() => setPicks((x) => Object.fromEntries(Object.entries(x).map(([k, v]) => [k, { ...v, selected: false }])))}>Clear</Button>
          </div>
          <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10, maxHeight: 540, overflow: "auto" }}>
            {dataCands.map(candidateRow)}
            {dataCands.length === 0 ? <div className="ds-list__row ds-muted">No data tables found — the sheets below look like summaries or notes. You can still tick one.</div> : null}
            {otherCands.length ? (
              <>
                <div className="ds-list__row is-clickable" style={{ background: "var(--surface-2, var(--surface))", fontWeight: 600 }} onClick={() => setShowOthers((v) => !v)}>
                  {showOthers || dataCands.length === 0 ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  <div className="ds-list__main">Also in this file · not ticked <span className="ds-faint" style={{ fontWeight: 400 }}>(pivots, formatted reports, notes)</span></div>
                  <span className="ds-small ds-faint">{otherCands.length}</span>
                </div>
                {showOthers || dataCands.length === 0 ? otherCands.map(candidateRow) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {step === "list" && list ? (
        <div className="ds-stack">
          <Field label="Table name" hint="How it appears in the designer."><Input value={listName} onChange={(e) => setListName(e.target.value)} autoFocus /></Field>
          {existingLists.has(list.id) ? <Banner tone="info" title="This list is already added">Adding it again just opens the existing table.</Banner> : null}
          <SheetPreview spec={{ kind: "sharepoint_list", tenant_id: tenantId, site_id: site!.id, site_name: site!.name, list_id: list.id, list_name: list.name }} sheet={null} header={1} />
        </div>
      ) : null}

      {step === "done" && result ? (
        <div className="ds-stack">
          <Banner tone="success" title={`${result.tables.filter((t) => t.status === "created").length} table${result.tables.filter((t) => t.status === "created").length === 1 ? "" : "s"} ready`}>
            Cleaned and typed on import{autoMeasures ? ", with totals and averages for every amount column" : ""}. In a report, add the whole file with one click from <em>Data → Add data</em>; links between its tables are made for you.
          </Banner>
          <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
            {result.tables.map((t) => (
              <div key={t.id} className="ds-list__row is-clickable" onClick={() => onOpenTable(t.id)}>
                {t.error ? <AlertTriangle size={16} color="var(--warning)" /> : <CheckCircle2 size={16} color="var(--success)" />}
                <div className="ds-list__main">
                  <div className="ds-list__title">{t.name}</div>
                  <div className="ds-list__meta">
                    {t.status === "existing" ? "was already added · " : ""}
                    {t.error ? `couldn't read columns: ${t.error}` : `${t.field_count} columns`}
                    {t.step_count ? ` · ${t.step_count} clean-up step${t.step_count === 1 ? "" : "s"}` : ""}
                    {t.measure_count ? ` · ${t.measure_count} measures` : ""}
                  </div>
                </div>
                <ChevronRight size={16} className="ds-faint" />
              </div>
            ))}
          </div>
          <div className="ds-small ds-muted">Next: open <Link to="/admin/reports" onClick={close}>Reports</Link> → New report → <em>Build from my data</em>.</div>
        </div>
      ) : null}
    </Modal>
  );
}

/** Applied steps (switch each on/off), header row for plain sheets, and the
 * rows as they'll be after the clean-up. */
function CandidateDetail({ spec, c, pick, onChange }: { spec: SourceSpec; c: Candidate; pick: Pick; onChange: (patch: Partial<Pick>) => void }) {
  const [pickHeader, setPickHeader] = useState(false);
  return (
    <div style={{ padding: "6px 16px 14px 48px", background: "var(--surface-2, transparent)" }} className="ds-stack">
      <div className="ds-row" style={{ gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ minWidth: 260, flex: "0 0 300px" }}>
          <div className="ds-small" style={{ fontWeight: 650, marginBottom: 6 }}>Applied steps</div>
          <div className="ds-stack" style={{ gap: 4 }}>
            <div className="ds-small ds-muted">{c.kind === "table" ? `Source · Excel table ${c.ref ?? ""}` : `Source · sheet “${c.sheet}”, column names on row ${pick.header}`}</div>
            {pick.steps.map((s, i) => (
              <Switch key={i} checked={s.is_active !== false} label={<span className="ds-small">{s.label ?? s.type.replace(/_/g, " ")}</span>}
                onChange={(v) => onChange({ steps: pick.steps.map((x, j) => (j === i ? { ...x, is_active: v } : x)) })} />
            ))}
            {pick.steps.length === 0 ? <div className="ds-small ds-faint">Already clean — nothing to fix.</div> : null}
            {c.kind === "sheet" ? (
              <Button size="sm" variant="ghost" icon={Settings2} onClick={() => setPickHeader((v) => !v)} style={{ alignSelf: "flex-start", marginTop: 4 }}>
                {pickHeader ? "Done choosing" : "Column names are on another row?"}
              </Button>
            ) : null}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 320 }}>
          {pickHeader && c.kind === "sheet" ? (
            <SheetPreview spec={spec} sheet={c.key} header={pick.header} onHeader={(h) => onChange({ header: h })} steps={pick.steps} />
          ) : pick.dirty ? (
            <SheetPreview spec={spec} sheet={c.key} header={pick.header} steps={pick.steps} cleanedOnly />
          ) : (
            <CleanedRows columns={c.columns} rows={c.sample} />
          )}
        </div>
      </div>
    </div>
  );
}

function CleanedRows({ columns, rows }: { columns: string[]; rows: Array<Record<string, unknown>> }) {
  const cols = columns.slice(0, 14);
  if (!rows.length) return <div className="ds-small ds-faint">No rows to show.</div>;
  return (
    <div style={{ overflow: "auto", maxHeight: 230, border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)" }}>
      <table className="ds-small" style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>{cols.map((c) => <th key={c} style={{ textAlign: "left", padding: "5px 8px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--surface)" }}>{c.trim()}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{cols.map((c) => <td key={c} style={{ padding: "4px 8px", borderBottom: "1px solid var(--divider)", whiteSpace: "nowrap", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{formatCell(r[c], c)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Codes and IDs (account 571000, year 2026, doc no.) without thousand separators. */
const LABEL_COLUMN = /(^|[\s_./#-])(id|no|nr|number|num|code|account|doc|document|ref|year|period|month|order|g\/l)($|[\s_./#-])/i;

function formatCell(v: unknown, column = ""): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    if (Number.isInteger(v) && LABEL_COLUMN.test(` ${column} `)) return String(v);
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T00:00:00$/.test(v)) return v.slice(0, 10);
  return String(v);
}

function SheetPreview({ spec, sheet, header, onHeader, steps, cleanedOnly }: { spec: SourceSpec; sheet: string | null; header: number; onHeader?: (h: number) => void; steps?: CleanStep[]; cleanedOnly?: boolean }) {
  const preview = useQuery<TablePreview>({
    queryKey: ["admin", "data-preview", spec.item_id ?? spec.list_id, sheet, header, JSON.stringify(steps ?? [])],
    queryFn: () => dataHubApi.preview(spec, sheet, header, steps),
    retry: false,
    staleTime: 60_000,
  });
  return (
    <div style={cleanedOnly || steps ? undefined : { padding: "4px 16px 14px 48px", background: "var(--surface-2, transparent)" }}>
      {preview.isLoading ? <SkeletonRows rows={3} /> : preview.isError ? (
        <Banner tone="danger" title="Couldn't read it">{preview.error instanceof Error ? preview.error.message : null}</Banner>
      ) : preview.data ? (
        <div className="ds-stack" style={{ gap: 10 }}>
          <div className="ds-row" style={{ flexWrap: "wrap", gap: 6 }}>
            <span className="ds-small ds-muted" style={{ marginRight: 4 }}>{preview.data.columns.length} columns:</span>
            {preview.data.columns.map((c) => <ColumnChip key={c.source_name} c={c} />)}
            {preview.data.columns.length === 0 ? <span className="ds-small">none found — try another header row</span> : null}
          </div>
          {onHeader && preview.data.raw_rows.length ? (
            <>
              <div className="ds-small ds-muted">The highlighted row is used as the column names. Click another row if the names are further down.</div>
              <div style={{ overflow: "auto", maxHeight: 220, border: "1px solid var(--border)", borderRadius: 8 }}>
                <table className="ds-small" style={{ borderCollapse: "collapse", width: "100%" }}>
                  <tbody>
                    {preview.data.raw_rows.map((r) => (
                      <tr key={r.row_number} onClick={() => onHeader(r.row_number)} style={{ cursor: "pointer", background: r.row_number === header ? "var(--accent-soft)" : undefined, fontWeight: r.row_number === header ? 650 : undefined }}>
                        <td className="ds-faint" style={{ padding: "4px 8px", borderBottom: "1px solid var(--divider)", whiteSpace: "nowrap" }}>{r.row_number}</td>
                        {r.values.slice(0, 12).map((v, i) => <td key={i} style={{ padding: "4px 8px", borderBottom: "1px solid var(--divider)", whiteSpace: "nowrap", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{v ?? ""}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : preview.data.rows?.length ? (
            <CleanedRows columns={preview.data.columns.map((c) => c.source_name)} rows={preview.data.rows} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ColumnChip({ c }: { c: DetectedColumn }) {
  const Icon = c.field_role === "identifier" ? KeyRound : c.field_role === "measure" ? Sigma : TYPE_ICON[c.data_type] ?? TypeIcon;
  const role = c.field_role === "measure" ? "number you can sum" : c.field_role === "date" ? "date" : c.field_role === "identifier" ? "ID" : "category";
  return (
    <span title={`${c.source_name} · ${role}`} className="ds-row" style={{ gap: 4, padding: "2px 8px", borderRadius: 999, border: "1px solid var(--border)", fontSize: 12 }}>
      <Icon size={12} className="ds-faint" /> {c.display_name}
    </span>
  );
}
